import { motion } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import {
  Trophy,
  Users,
  Building2,
  MessageCircle,
  Rocket,
  Zap,
} from "lucide-react";

// The six pillars that Prime Media Wireless leads with. Reused across
// Home, About, Become-a-Dealer, Total Wireless Master Agent, and every
// City Page so the messaging stays consistent site-wide. Pillar #1 is
// intentionally called out visually (larger tile + trophy icon + navy
// badge) so the "2026 Master Agent Award – Growth Champion" recognition
// leads every render.
const PILLARS = [
  {
    icon: Trophy,
    hero: true,
    label: "2026 Growth Champion",
    title: "The #1 Fastest-Growing Total Wireless Master Agent",
    body:
      "Prime Media Wireless has expanded significantly, earning the 2026 Master Agent Award — Growth Champion from Total Wireless (Verizon Value). We are actively scaling our dealer network nationwide and backing that footprint with a highly aggressive field-support strategy.",
    testid: "why-pillar-growth-champion",
  },
  {
    icon: Users,
    label: "Field Support Infrastructure",
    title: "More Boots on the Ground Than Any Other Master Agent",
    body:
      "We have deliberately prioritized boots-on-the-ground support, deploying a larger fleet of dedicated field-support agents and territory leaders to directly assist retail locations — hands-on, in-store, when it matters.",
    testid: "why-pillar-field-support",
  },
  {
    icon: Rocket,
    label: "Multi-Store Ready",
    title: "Built for Large-Scale, Corporate-Level Onboarding",
    body:
      "Prime Media Wireless is fully built out for large-scale, corporate-level dealer onboarding, using our Growth Champion momentum to rapidly integrate multi-store operators into the Total Wireless network.",
    testid: "why-pillar-corporate-onboarding",
  },
  {
    icon: MessageCircle,
    label: "Direct Executive Access",
    title: "You Get Leadership, Not a Phone Queue",
    body:
      "Dealers aren't left waiting in corporate phone queues. Prime Media Wireless is built on accessible, transparent communication from leadership down to the field — real people, real answers, real fast.",
    testid: "why-pillar-executive-access",
  },
  {
    icon: Building2,
    label: "Enterprise Onboarding",
    title: "A Dedicated Corporate Infrastructure for Multi-Unit Operators",
    body:
      "A dedicated corporate infrastructure built to rapidly transition large-scale, multi-unit operators under the Prime Media Wireless flag — with tools, teams, and playbooks that were designed for scale from day one.",
    testid: "why-pillar-enterprise",
  },
  {
    icon: Zap,
    label: "Actively Vetting",
    title: "Scaling With You as Total Wireless Grows on Verizon",
    body:
      "As Total Wireless continues its rapid national expansion on Verizon's network, Prime Media Wireless is actively vetting new sub-dealers, independent wireless retailers, and multi-store entrepreneurs looking to scale their portfolios.",
    testid: "why-pillar-vetting",
  },
];

export function WhyChoosePmw({
  heading = "Why Choose Prime Media Wireless",
  subheading = "Six reasons operators are moving their stores under the Prime Media Wireless flag.",
  className = "",
  variant = "default", // "default" | "compact"
}) {
  const compact = variant === "compact";
  return (
    <section
      className={`py-16 md:py-20 bg-white ${className}`}
      data-testid="why-choose-pmw-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mb-10">
          <p className="label-style text-teal mb-3">Why Prime Media Wireless</p>
          <h2 className="font-heading font-bold text-navy text-3xl md:text-4xl mb-3">
            {heading}
          </h2>
          {subheading && (
            <p className="text-slate-600 font-body text-base md:text-lg">
              {subheading}
            </p>
          )}
        </div>

        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {PILLARS.map((p, idx) => {
            const Icon = p.icon;
            const isHero = p.hero;
            return (
              <motion.div
                key={p.testid}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ delay: Math.min(idx, 5) * 0.05, duration: 0.35 }}
                className={
                  isHero
                    ? "md:col-span-2 lg:col-span-3"
                    : compact
                    ? "lg:col-span-1"
                    : ""
                }
              >
                <Card
                  className={
                    isHero
                      ? "border-teal/50 bg-gradient-to-br from-navy to-navy-dark text-white hover-lift"
                      : "border-slate-200 hover-lift bg-white"
                  }
                  data-testid={p.testid}
                >
                  <CardContent
                    className={`p-6 md:p-7 ${isHero ? "flex flex-col md:flex-row items-start gap-5" : ""}`}
                  >
                    <div
                      className={`inline-flex items-center justify-center rounded-xl flex-shrink-0 ${
                        isHero
                          ? "w-14 h-14 bg-teal/20 text-teal"
                          : "w-11 h-11 bg-teal/10 text-teal"
                      }`}
                    >
                      <Icon className={isHero ? "w-7 h-7" : "w-5 h-5"} />
                    </div>
                    <div className={`min-w-0 ${isHero ? "flex-1" : "mt-4"}`}>
                      {p.label && (
                        <span
                          className={`inline-block label-style text-[10px] px-2 py-0.5 rounded-full mb-2 ${
                            isHero
                              ? "bg-teal text-navy font-semibold"
                              : "bg-teal/10 text-teal"
                          }`}
                        >
                          {p.label}
                        </span>
                      )}
                      <h3
                        className={`font-heading font-semibold mb-2 ${
                          isHero
                            ? "text-white text-xl md:text-2xl"
                            : "text-navy text-lg"
                        }`}
                      >
                        {p.title}
                      </h3>
                      <p
                        className={`font-body ${
                          isHero
                            ? "text-slate-200 text-base md:text-lg leading-relaxed"
                            : "text-slate-600 text-sm leading-relaxed"
                        }`}
                      >
                        {p.body}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export default WhyChoosePmw;
